import classNames from "classnames";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";
import { ReactElement, useMemo } from "react";
import styles from "./index.module.scss";
import SvgIcon from "@/components/common/SvgIcon.tsx";
import { Typography } from "@/components/kit";

type ToastProps = {
  title?: string;
  description?: ReactElement[] | ReactElement | string;
  status: "warning" | "success" | "info" | "error";
  type?: "light" | "solid" | "left-bordered" | "top-bordered";
  className?: string;
  timeDelay?: number;
};

export const Toast = ({
  description,
  status,
  title,
  type = "solid",
  onClose,
  className,
  closeable,
}: ToastProps & { onClose?: () => void; closeable?: boolean }) => {
  type TSvgData = { name: string; fill: string };
  const svgData = useMemo((): TSvgData => {
    if (status === "info") {
      return {
        name: "infoFill",
        fill: type === "solid" ? "fill-white" : "fill-blue-600",
      };
    }
    if (status === "warning") {
      return {
        name: "infoFill",
        fill: type === "solid" ? "fill-white" : "fill-orange-500",
      };
    }
    if (status === "error") {
      return {
        name: "closeCircleRed",
        fill: type === "solid" ? "fill-white" : "fill-red-500",
      };
    }
    return {
      name: "checkFill",
      fill: type === "solid" ? "fill-white" : "fill-green-600",
    };
  }, [status, type]);
  return (
    <div
      className={classNames(
        " rounded-[6px] p-3",
        {
          "bg-orange-100 border-orange-600 text-orange-800":
            status === "warning" && type !== "solid",
          "bg-orange-500": status === "warning" && type === "solid",
          "bg-blue-100 border-blue-600": status === "info" && type !== "solid",
          "bg-blue-600": status === "info" && type === "solid",
          "bg-green-100 border-green-600":
            status === "success" && type !== "solid",
          "bg-green-600": status === "success" && type === "solid",
          "bg-red-100 border-red-600": status === "error" && type !== "solid",
          "bg-red-500": status === "error" && type === "solid",
          "border-t-[4px]": type === "top-bordered",
          "border-l-[4px]": type === "left-bordered",
          warning: status === "warning",
          success: status === "success",
          info: status === "info",
          error: status === "error",
          solid: type === "solid",
        },
        className,
      )}
    >
      <div className="flex gap-x-2 items-start">
        <SvgIcon
          className={"w-6 h-6 mt-1"}
          iconName={svgData.name}
          fill={svgData.fill}
        />
        <div
          className={classNames("text-left flex-grow", {
            "text-white": type === "solid",
          })}
        >
          {title && (
            <Typography
              color={type === "solid" ? "text-white" : "text-gray-800"}
              block
              variant={"BodyPlus1-bold"}
            >
              {title}
            </Typography>
          )}
          {description && (
            <Typography
              color={type === "solid" ? "text-white" : "text-gray-800"}
              block
              variant={"BodyPlus1-Regular"}
              className="first-letter:capitalize"
            >
              {typeof description === "string"
                ? description.toLowerCase()
                : description}
            </Typography>
          )}
        </div>
        {closeable && (
          <svg
            onClick={onClose}
            width="16"
            height="16"
            viewBox="0 0 16 16"
            xmlns="http://www.w3.org/2000/svg"
            className={classNames("cursor-pointer flex-shrink-0", {
              "fill-white": type === "solid",
            })}
          >
            <path d="M8.00048 7.05732L11.3005 3.75732L12.2431 4.69999L8.94315 7.99999L12.2431 11.3L11.3005 12.2427L8.00048 8.94266L4.70048 12.2427L3.75781 11.3L7.05781 7.99999L3.75781 4.69999L4.70048 3.75732L8.00048 7.05732Z" />
          </svg>
        )}
      </div>
    </div>
  );
};

const Toaster = (data: ToastProps) => {
  const toastContainer = document.querySelector("#portal")!;
  const root = createRoot(toastContainer);

  const delay = typeof data.timeDelay === "number" ? data.timeDelay : 6000;

  const timer = setTimeout(() => {
    closeToaster();
  }, delay);
  const closeToaster = () => {
    clearTimeout(timer);
    root.unmount();
    document.querySelector("#portal div")?.remove();
  };
  return root.render(
    createPortal(
      <Toast
        className={`${styles.toaster} z-[var(--toast-zIndex)] w-[356px]`}
        closeable
        onClose={closeToaster}
        {...data}
      />,
      document.querySelector("#portal")!,
    ),
  );
};

export default Toaster;
